import { useState, useEffect, useCallback } from 'react';
import { 
    initDB, 
    addPendingEntry, 
    getPendingEntries, 
    removePendingEntry,
    cacheCases,
    getCachedCases,
    getPendingCount
} from '../utils/offlineDB';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const useOfflineSync = (getAuthHeaders) => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isSyncing, setIsSyncing] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);

    // Initialize DB on mount
    useEffect(() => {
        initDB().catch(console.error);
        updatePendingCount();
    }, []);

    // Listen for online/offline events
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            toast.success('Back online');
            syncPendingEntries();
        };

        const handleOffline = () => {
            setIsOnline(false);
            toast.warning('You are offline', {
                description: 'Time entries will be saved locally'
            });
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Update pending count
    const updatePendingCount = useCallback(async () => {
        try {
            const count = await getPendingCount();
            setPendingCount(count);
        } catch (error) {
            console.error('Failed to get pending count:', error);
        }
    }, []);

    // Sync pending entries to server
    const syncPendingEntries = useCallback(async () => {
        if (!navigator.onLine || isSyncing) return;

        setIsSyncing(true);
        let syncedCount = 0;
        let failedCount = 0;

        try {
            const pending = await getPendingEntries();
            
            if (pending.length === 0) {
                setIsSyncing(false);
                return;
            }

            const headers = getAuthHeaders ? getAuthHeaders() : {};

            for (const entry of pending) {
                try {
                    // Prepare the entry data for the API
                    const entryData = {
                        case_id: entry.case_id,
                        task_type: entry.task_type,
                        duration_minutes: entry.duration_minutes,
                        notes: entry.notes || '',
                        is_manual: true
                    };

                    const response = await fetch(`${API}/time-entries/manual`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...headers
                        },
                        body: JSON.stringify(entryData)
                    });

                    if (response.ok) {
                        await removePendingEntry(entry.localId);
                        syncedCount++;
                    } else {
                        failedCount++;
                    }
                } catch (error) {
                    console.error('Failed to sync entry:', error);
                    failedCount++;
                }
            }

            await updatePendingCount();

            if (syncedCount > 0) {
                toast.success(`Synced ${syncedCount} time ${syncedCount === 1 ? 'entry' : 'entries'}`);
            }

            if (failedCount > 0) {
                toast.error(`Failed to sync ${failedCount} ${failedCount === 1 ? 'entry' : 'entries'}`);
            }
        } catch (error) {
            console.error('Sync failed:', error);
        } finally {
            setIsSyncing(false);
        }
    }, [getAuthHeaders, isSyncing, updatePendingCount]);

    // Queue entry for offline sync
    const queueTimeEntry = useCallback(async (entryData) => {
        try {
            await addPendingEntry(entryData);
            await updatePendingCount();
            toast.info('Time entry saved offline', {
                description: 'Will sync when back online'
            });
            return true;
        } catch (error) {
            console.error('Failed to queue entry:', error);
            toast.error('Failed to save offline');
            return false;
        }
    }, [updatePendingCount]);

    // Cache cases for offline use
    const cacheForOffline = useCallback(async (cases) => {
        try {
            await cacheCases(cases);
        } catch (error) {
            console.error('Failed to cache cases:', error);
        }
    }, []);

    // Get cached cases when offline
    const getOfflineCases = useCallback(async () => {
        try {
            return await getCachedCases();
        } catch (error) {
            console.error('Failed to get cached cases:', error);
            return [];
        }
    }, []);

    // Manual sync trigger
    const triggerSync = useCallback(() => {
        if (isOnline) {
            syncPendingEntries();
        }
    }, [isOnline, syncPendingEntries]);

    return {
        isOnline,
        isSyncing,
        pendingCount,
        queueTimeEntry,
        syncPendingEntries,
        triggerSync,
        cacheForOffline,
        getOfflineCases,
        updatePendingCount
    };
};

export default useOfflineSync;
